import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Container,
  Chip,
  Alert,
} from '@mui/material';
import {
  Block as BlockIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

const AUTO_CHECK_INTERVAL_MS = 15000;

export default function PaymentBlocked({ company, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchPendingPayment();
  }, []);

  useEffect(() => {
    if (!payment?.id) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      checkPaymentStatus({ silent: true });
    }, AUTO_CHECK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [payment?.id]);

  const fetchPendingPayment = async () => {
    try {
      // Endpoint público: a empresa bloqueada não tem token autenticado.
      const response = await axios.post(
        `${API}/payments/companies/${company.id}/check-status`
      );

      if (response.data.approved || response.data.company_payment_status === "em_dia") {
        toast.success("Pagamento confirmado! Recarregando...");
        window.location.reload();
        return;
      }

      setPayment(response.data.payment);
    } catch (error) {
      // Sem pagamento pendente
      setPayment(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePix = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/companies/${company.id}/generate-pix`);
      setPayment(response.data.payment);
      toast.success("QR Code PIX gerado com sucesso!");
    } catch (error) {
      console.error("Erro ao gerar PIX", error);

      toast.error("Erro ao gerar PIX. Entre em contato com o suporte.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPix = () => {
    if (payment?.qr_code) {
      navigator.clipboard.writeText(payment.qr_code);
      toast.success("Código PIX copiado!");
    }
  };

  const checkPaymentStatus = async ({ silent = false } = {}) => {
    if (!silent) {
      setCheckingPayment(true);
    }

    try {
      // Endpoint público (empresa bloqueada não possui token).
      const response = await axios.post(
        `${API}/payments/companies/${company.id}/check-status`
      );

      if (response.data.payment) {
        setPayment(response.data.payment);
      }

      if (response.data.approved || response.data.company_payment_status === 'em_dia') {
        toast.success("Pagamento confirmado! Recarregando...");
        window.location.reload();
        return;
      }

      if (!silent) {
        toast.info("Pagamento ainda não identificado. Aguarde alguns instantes.");
      }
    } catch (error) {
      if (!silent) {
        toast.info("Pagamento ainda pendente");
      }
    } finally {
      if (!silent) {
        setCheckingPayment(false);
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Card elevation={4}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box textAlign="center" mb={4}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'error.lighter',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 2,
                }}
              >
                <BlockIcon sx={{ fontSize: 50, color: 'error.main' }} />
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Acesso Bloqueado
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {company?.name}
              </Typography>
              <Chip
                label={company?.payment_status === 'bloqueada' ? 'Bloqueada' : 'Em Atraso'}
                color="error"
                sx={{ mt: 2 }}
              />
            </Box>

            {/* Message */}
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body1">
                Seu acesso está bloqueado por falta de pagamento.
                Regularize sua mensalidade para voltar a usar o sistema.
              </Typography>
            </Alert>

            {/* Payment Info */}
            {loading ? (
              <Box textAlign="center" py={4}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" mt={2}>
                  Carregando informações de pagamento...
                </Typography>
              </Box>
            ) : payment ? (
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom textAlign="center">
                  Pague com PIX
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                  Valor: <strong>R$ 90,00</strong>
                </Typography>

                {/* QR Code */}
                {payment.qr_code_base64 && (
                  <Box display="flex" justifyContent="center" textAlign="center" mb={3}>
                    <Box
                      component="img"
                      src={`data:image/png;base64,${payment.qr_code_base64}`}
                      alt="QR Code PIX"
                      sx={{
                        maxWidth: 300,
                        width: '100%',
                        border: 2,
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                      }}
                    />
                  </Box>
                )}

                {/* Pix Code */}
                {payment.qr_code && (
                  <Box mb={3}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Ou copie o código:
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: 'grey.100',
                        p: 2,
                        borderRadius: 2,
                        wordBreak: 'break-all',
                        fontSize: '0.85rem',
                        fontFamily: 'monospace',
                        maxHeight: 100,
                        overflow: 'auto',
                      }}
                    >
                      {payment.qr_code}
                    </Box>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CopyIcon />}
                      onClick={handleCopyPix}
                      sx={{ mt: 2 }}
                    >
                      Copiar Código PIX
                    </Button>
                  </Box>
                )}

                {/* Check Payment Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={checkingPayment ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                  onClick={() => checkPaymentStatus()}
                  disabled={checkingPayment}
                  sx={{ mb: 2 }}
                >
                  {checkingPayment ? "Verificando..." : "Já Paguei - Verificar Pagamento"}
                </Button>

                <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                  O pagamento pode levar alguns minutos para ser confirmado
                </Typography>
              </Box>
            ) : (
              <Box textAlign="center" py={3}>
                <Typography variant="body1" gutterBottom>
                  Nenhuma cobrança pendente encontrada
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleGeneratePix}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  Gerar Nova Cobrança
                </Button>
              </Box>
            )}

            {/* Logout */}
            <Box textAlign="center" mt={4}>
              <Button variant="text" onClick={onLogout}>
                Sair
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
